import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import {
  GridCsvExportMenuItemProps,
  GridCsvExportOptions,
  useGridApiContext,
} from "@mui/x-data-grid-pro";
import { useTranslation } from "react-i18next";
import { buildGridWorkbookOptions } from "./buildGridWorkbookOptions";
import { WorkbookRow, createWorkbook, downloadBlob } from "@/utilities/xlsx";

export const EXCEL_IGNORED_COLUMN_LABELS = "Defekte, Addresse, Warnungen, Zonen, Restreichweite";
const EXCLUDED_EXPORT_FIELDS = new Set([
  "maintenance",
  "address",
  "warnings",
  "zones",
  "remainingRange",
]);
type GridApiRefLike = ReturnType<typeof useGridApiContext>;

export interface GridExcelExportProps extends GridCsvExportMenuItemProps {
  /**
   * Rows written above the table, followed by a blank one — where a report says which database
   * and which period it came from. A sheet that has been mailed on has no other way of telling
   * its reader that, and a figure whose period is unknown cannot be checked against anything.
   */
  metadata?: WorkbookRow[];
}

export function exportGridAsExcel(
  apiRef: GridApiRefLike,
  options?: GridCsvExportMenuItemProps["options"],
  metadata?: WorkbookRow[],
) {
  const { csvOptions, exportedFields, columnTypes } = getExcelExportOptions(apiRef, options);
  const csv = apiRef.current.getDataAsCsv(csvOptions);
  const parsedRows = parseCsv(csv, csvOptions.delimiter ?? ",");
  const includeHeaders = csvOptions.includeHeaders ?? true;
  const tableRows = sanitizeExportRows(parsedRows, exportedFields, includeHeaders);

  const preamble: WorkbookRow[] = metadata?.length ? [...metadata, [""]] : [];
  const workbookOptions = buildGridWorkbookOptions({
    exportedFields,
    columnTypes,
    includeHeaders,
    firstRow: preamble.length + 1,
  });

  downloadBlob(
    createWorkbook([...preamble, ...tableRows], workbookOptions),
    toXlsxFileName(csvOptions.fileName),
  );
}

export default function GridExcelExportMenuItem(props: GridExcelExportProps) {
  const apiRef = useGridApiContext();
  const { t } = useTranslation();
  const { hideMenu, options, metadata, ...other } = props;

  const onClick = () => {
    exportGridAsExcel(apiRef, options, metadata);
    hideMenu?.();
  };

  return (
    <Tooltip
      title={t(
        "common.excelIgnoredColumnsTooltip",
        `Ignoriert beim Excel-Export: ${EXCEL_IGNORED_COLUMN_LABELS}`,
      )}
      placement="right"
    >
      <MenuItem onClick={onClick} {...other}>
        {t("common.downloadAsExcel", "Download als Excel")}
      </MenuItem>
    </Tooltip>
  );
}

function getExcelExportOptions(
  apiRef: ReturnType<typeof useGridApiContext>,
  options?: GridCsvExportMenuItemProps["options"],
) {
  const allColumns = apiRef.current.getAllColumns();

  const baseFields =
    options?.fields && options.fields.length > 0
      ? options.fields
      : (options?.allColumns ? allColumns : apiRef.current.getVisibleColumns())
          .filter(column => !column.disableExport)
          .map(column => column.field);

  const exportedFields = baseFields.filter(field => !EXCLUDED_EXPORT_FIELDS.has(field));
  const { disableToolbarButton: _ignoredToolbarSetting, ...csvOptionsWithoutToolbar } =
    options ?? {};

  const csvOptions: GridCsvExportOptions = {
    ...csvOptionsWithoutToolbar,
    fields: exportedFields,
    // Text cells are written as `inlineStr`, which Excel never evaluates, and numeric cells hold
    // a number rather than an expression — so CSV formula escaping is not needed here.
    escapeFormulas: false,
  };

  // The grid already knows which columns are numbers; taking it from here beats guessing from
  // the exported strings, which would turn serial numbers into integers.
  const columnTypes = allColumns.reduce<Record<string, string | undefined>>((types, column) => {
    types[column.field] = column.type;
    return types;
  }, {});

  return { csvOptions, exportedFields, columnTypes };
}

function sanitizeExportRows(rows: string[][], exportedFields: string[], includeHeaders: boolean) {
  const statusColumnIndex = exportedFields.indexOf("vehicleStatus");
  if (statusColumnIndex === -1) return rows;

  const dataStartIndex = includeHeaders ? 1 : 0;
  for (let rowIndex = dataStartIndex; rowIndex < rows.length; rowIndex++) {
    const cellValue = rows[rowIndex]?.[statusColumnIndex];
    if (cellValue == null) continue;
    const normalized = cellValue.trim();
    if (normalized === "---" || normalized === "__NONE__") {
      rows[rowIndex][statusColumnIndex] = "";
    }
  }

  return rows;
}

function toXlsxFileName(fileName?: string) {
  const fromOptions = fileName?.trim() ?? "";
  const fromDocument = document.title?.trim() ?? "";
  const baseName = (fromOptions || fromDocument || "export").replace(/\.[^/.]+$/, "");
  return `${baseName}.xlsx`;
}

function parseCsv(csvContent: string, delimiter: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let i = 0;
  let inQuotes = false;

  while (i < csvContent.length) {
    const char = csvContent[i];

    if (inQuotes) {
      if (char === "\"") {
        if (csvContent[i + 1] === "\"") {
          cell += "\"";
          i += 2;
          continue;
        }
        inQuotes = false;
        i += 1;
        continue;
      }
      cell += char;
      i += 1;
      continue;
    }

    if (char === "\"") {
      inQuotes = true;
      i += 1;
      continue;
    }

    if (char === delimiter) {
      row.push(cell);
      cell = "";
      i += 1;
      continue;
    }

    if (char === "\n" || char === "\r") {
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";

      if (char === "\r" && csvContent[i + 1] === "\n") {
        i += 2;
      } else {
        i += 1;
      }
      continue;
    }

    cell += char;
    i += 1;
  }

  if (cell.length > 0 || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }

  return rows;
}
